import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface LanguageStatus {
  language: string;
  phq9Completed: number;
  gad7Completed: number;
  phq9Total: number;
  gad7Total: number;
}

export function TranslationsPage() {
  const [langs, setLangs] = useState<LanguageStatus[]>([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [selectedType, setSelectedType] = useState('phq9');
  const [questions, setQuestions] = useState<string[]>([]);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    adminApi<{ data: LanguageStatus[] }>('/translations/admin/pending')
      .then((d) => setLangs(ensureArray(d.data)))
      .catch(() => setLangs([]));
  }, []);

  const loadQuestions = async (lang: string, type: string) => {
    setSelectedLang(lang);
    setSelectedType(type);
    try {
      const data = await adminApi<{ questions?: string[] }>(
        `/translations/${type}/${lang}?validated=false`
      );
      setQuestions(ensureArray(data.questions));
      setEdits({});
    } catch {
      setQuestions([]);
    }
  };

  const submitTranslation = async (idx: number) => {
    const text = edits[idx];
    if (!text) return;
    await adminApi(`/translations/${selectedType}/${selectedLang}`, {
      method: 'POST',
      body: JSON.stringify({ questionIndex: idx, questionText: text }),
    });
    setMsg(`Question ${idx + 1} saved — pending clinical approval`);
    setTimeout(() => setMsg(''), 3000);
    setEdits((prev) => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const approveTranslation = async (idx: number) => {
    await adminApi(`/translations/${selectedType}/${selectedLang}/${idx}/approve`, {
      method: 'POST',
    });
    setMsg(`Question ${idx + 1} approved for production`);
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Translation Management</h1>
      {msg && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-800">{msg}</div>}

      <div className="card mb-6">
        <h2 className="mb-3 text-lg font-semibold">Language Status</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {langs.map((l) => {
            const phq9Pct = l.phq9Total ? Math.round((l.phq9Completed / l.phq9Total) * 100) : 0;
            const gad7Pct = l.gad7Total ? Math.round((l.gad7Completed / l.gad7Total) * 100) : 0;
            return (
              <div key={l.language} className="rounded-lg border p-4">
                <p className="font-medium text-gray-900">{l.language.toUpperCase()}</p>
                <p className="text-sm text-gray-500">
                  PHQ-9: {l.phq9Completed}/{l.phq9Total} ({phq9Pct}%)
                </p>
                <p className="text-sm text-gray-500">
                  GAD-7: {l.gad7Completed}/{l.gad7Total} ({gad7Pct}%)
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => loadQuestions(l.language, 'phq9')}
                    className="btn-primary text-xs"
                  >
                    Edit PHQ-9
                  </button>
                  <button
                    onClick={() => loadQuestions(l.language, 'gad7')}
                    className="btn-primary text-xs"
                  >
                    Edit GAD-7
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedLang && (
        <div className="card">
          <h2 className="mb-3 text-lg font-semibold">
            {selectedType.toUpperCase()} — {selectedLang.toUpperCase()}
          </h2>
          <div className="space-y-3">
            {questions.map((q, idx) => (
              <div key={idx} className="rounded-lg border p-3">
                <p className="mb-1 text-sm text-gray-500">Q{idx + 1}</p>
                <p className="mb-2 text-sm text-gray-700">{q}</p>
                <textarea
                  className="w-full rounded-lg border p-2 text-sm"
                  rows={2}
                  placeholder="Edit translation..."
                  value={edits[idx] ?? ''}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [idx]: e.target.value }))}
                />
                <div className="mt-1 flex gap-2">
                  <button
                    onClick={() => submitTranslation(idx)}
                    disabled={!edits[idx]}
                    className="bg-primary-600 rounded px-3 py-1 text-xs text-white disabled:opacity-50"
                  >
                    Save Draft
                  </button>
                  <button
                    onClick={() => approveTranslation(idx)}
                    className="rounded bg-green-600 px-3 py-1 text-xs text-white"
                  >
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
